import React from 'react'
import classnames from 'classnames'
import { string, oneOf, number, bool, func } from 'prop-types'

import isDOMAvailable from 'utils/isDOMAvailable'
import { Card, CardBlock, Image } from 'components/qstrap'
import Typography from 'components/atoms/Typography'
import Markdown from 'components/atoms/MarkdownContent'
import Ellipsis from 'components/atoms/Ellipsis'

const Identity = ({
  name,
  jobTitle,
  schoolName,
  majorName,
  year,
}) => (
  <div className="testimony-identity">
    <Typography component="p" variant="body" className="testimony-identity__name font-weight-bold">
      {name}
      { year ? ` (${year})` : ''}
    </Typography>
    {
      jobTitle &&
      <Typography component="p" variant="subbody" className="font-weight-bold">
        { jobTitle }
      </Typography>
    }
    {
      schoolName &&
      <Typography component="p" variant="subbody" className="testimony-identity__school">
        { schoolName }
      </Typography>
    }
    { majorName &&
      <Typography component="p" variant="subbody" className="testimony-identity__major">
        { majorName }
      </Typography>
    }
    <style jsx global>
      {`
        .testimony-identity {
          &__name {
            color: #0c2846;
          }
        }
      `}
    </style>
  </div>
)

const Testimony = ({
    testimony,
    testimonyTitle,
    name,
    direction,
    img,
    imgAlt,
    schoolName,
    jobTitle,
    year,
    majorName,
    showMore,
    onClick,
    rowContent,
  }) => {
  const identity = {
    name,
    schoolName,
    majorName,
    jobTitle,
    year,
  }
  const withoutSiteUrl = !!img
  const className = classnames('testimony__content p-a-md', {
    flex: (direction === 'column') || rowContent,
  })
  return (
    <div className="testimony">
      <Card className="testimony__card">
        <CardBlock className={className}>
          <div className="testimony__head">
            <div className="testimony__avatar">
              <Image src={img || '/assets/img/placeholder-profile.svg'} alt={imgAlt || name} className="testimony__photo" withoutSiteUrl={withoutSiteUrl}/>
            </div>
            {
              (direction === 'row' || rowContent) &&
              <Identity {...identity} />
            }
          </div>
          <div className="testimony__body">
            {
              direction === 'column' &&
              <Identity {...identity} />
            }
            { testimonyTitle && <Typography variant="subbody" component="p" className="testimony__title-desc">{testimonyTitle}</Typography>}
            { (isDOMAvailable && window.outerWidth >= 768) || showMore || !onClick ?
              <Markdown content={testimony} className="testimony__description" /> :
              <Ellipsis text={testimony} clamp={5} markdown className="testimony__description" />
            }
            {
              onClick && testimony.length > 135 &&
              <span
                href="#"
                className="testimony__show-more"
                onClick={onClick}
              >
                { showMore ? 'Tutup...' : 'Baca Selengkapnya...' }
              </span>
            }
          </div>
        </CardBlock>
      </Card>
      <style jsx>
        {`
          .testimony {
            :global(&__card) {
              box-shadow: 0 4px 8px 0 rgba(107, 143, 188, 0.4);
              width: 100%;
              height: 100%;
              display: flex;
              margin-bottom: ${direction === 'row' ? '0 !important' : '0.75rem'};
            }
            :global(&__content) {
              width: 100%;
            }
            &__head {
              display: flex;
              flex: ${rowContent ? '1 1 80%' : ''};
              align-items: ${rowContent ? 'center' : ''};
            }
            &__avatar {
              margin: ${(direction === 'row' || rowContent) ? '0 16px 0 8px' : '0 26px'};
            }
            &__body {
              padding: 0 8px;
            }
            :global(&__title-desc) {
              color: #485c77 !important;
              margin-top: 16px !important;
              font-weight: bold;
            }
            :global(&__description) {
              :global(p) {
                font-size: 14px !important;
                color: #0c2846;
              }
              margin: 16px 0;
              line-height: 2;
            }
            :global(img) {
              width: ${(direction === 'row' || rowContent) ? '64' : '96'}px;
              height: ${(direction === 'row' || rowContent) ? '64' : '96'}px;
              border-radius: 100%;
            }
            :global(.Typography) {
              line-height: ${(direction === 'row' || rowContent) ? '1.5' : '2'};
            }
            :global(&__show-more) {
              display: none;
            }
            @media(max-width: 768px) {
              :global(&__content) {
                display: initial;
              }
              &__avatar {
                margin: ${direction === 'column' ? '0 16px 0 0' : '0 16px 0 8px'};
              }
              &__body {
                padding: 0;
              }
              &__show-more {
                display: block;
                font-size: 14px;
                font-weight: bold;
                color: #0fc3c4;
                text-align: center;
              }
            }
            :global(.flex) {
              display: flex;
            }
          }
        `}
      </style>
    </div>
  )
}

Identity.propTypes = {
  jobTitle: string,
  majorName: string,
  name: string,
  schoolName: string,
  year: number,
}

Testimony.propTypes = {
  testimony: string,
  testimonyTitle: string,
  name: string,
  img: string,
  imgAlt: string,
  majorName: string,
  schoolName: string,
  showMore: bool,
  jobTitle: string,
  direction: oneOf(['column', 'row']),
  year: number,
  onClick: func,
  rowContent: bool,
}

export default Testimony
